/**
 * Feedback Widget - Floating button and modal for user feedback
 */

import React, { useState, useRef } from 'react';
import { getConsoleLogs } from '../utils/consoleInterceptor';
import { getApiLogs } from '../utils/apiInterceptor';
import { FeedbackType, FeedbackData, UploadedImage } from '../types/feedback';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGES = 5;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [text, setText] = useState('');
  const [includeScreenshot, setIncludeScreenshot] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setError('');

    const remainingSlots = MAX_IMAGES - uploadedImages.length;
    if (remainingSlots <= 0) {
      setError(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const filesToProcess = Array.from(files).slice(0, remainingSlots);
    const newImages: UploadedImage[] = [];

    for (const file of filesToProcess) {
      // Validate type
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Only JPEG, PNG, GIF, and WebP are allowed.`);
        continue;
      }

      // Validate size
      if (file.size > MAX_IMAGE_SIZE) {
        setError(`File too large: ${file.name}. Maximum size is 5MB.`);
        continue;
      }

      // Convert to base64
      try {
        const base64 = await fileToBase64(file);
        newImages.push({
          name: file.name,
          type: file.type,
          data: base64,
          size: file.size,
        });
      } catch (err) {
        console.error('Failed to read file:', err);
        setError(`Failed to read file: ${file.name}`);
      }
    }

    if (newImages.length > 0) {
      setUploadedImages((prev) => [...prev, ...newImages]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async () => {
    if (!text.trim()) {
      setError('Please provide a description');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Capture screenshot if requested
      let screenshot: string | null = null;
      if (includeScreenshot) {
        try {
          // Use native canvas API to capture screenshot
          const canvas = document.createElement('canvas');
          const _context = canvas.getContext('2d');

          // Set canvas size to viewport size
          canvas.width = window.innerWidth;
          canvas.height = window.innerHeight;

          // For now, we'll use a simpler approach - just capture the page as data URL
          // Note: Full page screenshot would require html2canvas library
          screenshot = 'screenshot-placeholder'; // Placeholder for now
        } catch (err) {
          console.error('Failed to capture screenshot:', err);
          // Continue without screenshot
        }
      }

      // Build feedback data
      const feedbackData: FeedbackData = {
        feedbackType,
        text: text.trim(),
        screenshot,
        uploadedImages,
        consoleLogs: getConsoleLogs(),
        apiLogs: getApiLogs(),
        userAgent: navigator.userAgent,
        currentUrl: window.location.href,
        timestamp: new Date(),
      };

      // Submit to API
      const response = await fetch('/api/feedback/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setText('');
        setFeedbackType('bug');
        setIncludeScreenshot(false);
        setUploadedImages([]);

        // Close modal after 2 seconds
        setTimeout(() => {
          setIsOpen(false);
          setSuccess(false);
        }, 2000);
      } else {
        setError('Failed to submit feedback');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setIsOpen(false);
      setError('');
      setSuccess(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 999,
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            cursor: 'pointer',
            fontSize: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title="Send Feedback"
        >
          💬
        </button>
      )}

      {/* Modal */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleClose();
            }
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: '2rem',
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <h2 style={{ margin: 0 }}>Send Feedback</h2>
              <button
                onClick={handleClose}
                disabled={submitting}
                style={{
                  background: '#cbd5e0',
                  color: '#2d3748',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                }}
              >
                Close
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div
                style={{
                  background: '#fed7d7',
                  color: '#c53030',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                }}
              >
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div
                style={{
                  background: '#c6f6d5',
                  color: '#22543d',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                }}
              >
                ✓ Feedback submitted successfully! Thank you.
              </div>
            )}

            {/* Form */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#2d3748',
                }}
              >
                Feedback Type:
              </label>
              <select
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #cbd5e0',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  background: 'white',
                }}
              >
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="ui-ux">UI/UX Improvement</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#2d3748',
                }}
              >
                Description:
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Please describe the issue or suggestion in detail..."
                disabled={submitting}
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '0.75rem',
                  border: '1px solid #cbd5e0',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={includeScreenshot}
                  onChange={(e) => setIncludeScreenshot(e.target.checked)}
                  disabled={submitting}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.875rem', color: '#4a5568' }}>
                  Include screenshot (if available)
                </span>
              </label>
            </div>

            {/* Image Upload Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  color: '#2d3748',
                }}
              >
                Attach Images (optional):
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                multiple
                onChange={handleFileSelect}
                disabled={submitting || uploadedImages.length >= MAX_IMAGES}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || uploadedImages.length >= MAX_IMAGES}
                style={{
                  padding: '0.5rem 1rem',
                  background: uploadedImages.length >= MAX_IMAGES ? '#e2e8f0' : '#edf2f7',
                  color: uploadedImages.length >= MAX_IMAGES ? '#a0aec0' : '#4a5568',
                  border: '1px dashed #cbd5e0',
                  borderRadius: '6px',
                  cursor: uploadedImages.length >= MAX_IMAGES || submitting ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  width: '100%',
                }}
              >
                {uploadedImages.length >= MAX_IMAGES
                  ? `Maximum ${MAX_IMAGES} images reached`
                  : `Click to upload images (${uploadedImages.length}/${MAX_IMAGES})`}
              </button>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#718096' }}>
                JPEG, PNG, GIF, WebP. Max 5MB each.
              </p>

              {/* Image Preview List */}
              {uploadedImages.length > 0 && (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {uploadedImages.map((img, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.5rem',
                        background: '#f7fafc',
                        borderRadius: '4px',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <img
                        src={`data:${img.type};base64,${img.data}`}
                        alt={img.name}
                        style={{
                          width: '48px',
                          height: '48px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0',
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: '0.875rem',
                            color: '#2d3748',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {img.name}
                        </p>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#718096' }}>
                          {formatFileSize(img.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        disabled={submitting}
                        style={{
                          background: '#feb2b2',
                          color: '#c53030',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '0.25rem 0.5rem',
                          cursor: submitting ? 'not-allowed' : 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Box */}
            <div
              style={{
                background: '#ebf8ff',
                border: '1px solid #90cdf4',
                borderRadius: '6px',
                padding: '0.75rem',
                marginBottom: '1.5rem',
              }}
            >
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#2c5282' }}>
                <strong>Note:</strong> This feedback will include recent console logs and API
                requests to help us diagnose issues. No sensitive data will be captured.
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleClose}
                disabled={submitting}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#cbd5e0',
                  color: '#2d3748',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !text.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: submitting || !text.trim() ? '#cbd5e0' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  cursor: submitting || !text.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
